<template>
  <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <v-card-title>
        <span class="headline">Chat Info</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Direct Chat Info -->
        <div v-if="conversation && conversation.type === 'direct'">
          <div class="text-center mb-4">
            <v-avatar size="80" class="mb-3">
              <v-img
                v-if="conversation.user.profileimg"
                :src="conversation.user.profileimg"
              ></v-img>
              <v-icon v-else size="40">mdi-account</v-icon>
            </v-avatar>
            <h3>{{ conversation.user.Name }}</h3>
            <p class="grey--text">{{ conversation.user.Email }}</p>
            <v-chip
              v-if="conversation.user.isOnline"
              small
              color="green"
              text-color="white"
            >
              Online
            </v-chip>
            <p v-else class="caption grey--text">
              Last seen {{ formatLastSeen(conversation.user.lastSeen) }}
            </p>
          </div>
        </div>

        <!-- Group Chat Info -->
        <div v-else-if="conversation && conversation.type === 'group'">
          <div class="text-center mb-4">
            <v-avatar size="80" class="mb-3">
              <v-img
                v-if="conversation.group.avatar"
                :src="conversation.group.avatar"
              ></v-img>
              <v-icon v-else size="40">mdi-account-group</v-icon>
            </v-avatar>
            <h3>{{ conversation.group.name }}</h3>
            <p v-if="conversation.group.description" class="grey--text">
              {{ conversation.group.description }}
            </p>
            <p class="caption grey--text">
              {{ conversation.group.members.length }} members
            </p>
          </div>

          <!-- Group Members -->
          <v-divider class="mb-4"></v-divider>
          <h4 class="mb-3">Members</h4>
          <v-list dense>
            <v-list-item
              v-for="member in conversation.group.members"
              :key="member.user._id"
            >
              <v-list-item-avatar>
                <v-img
                  v-if="member.user.profileimg"
                  :src="member.user.profileimg"
                ></v-img>
                <v-avatar v-else color="primary">
                  <v-icon>mdi-account</v-icon>
                </v-avatar>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title>
                  {{ member.user.Name }}
                  <v-chip
                    v-if="member.role === 'admin'"
                    x-small
                    color="primary"
                    class="ml-2"
                  >
                    Admin
                  </v-chip>
                  <v-chip
                    v-if="conversation.group.creator === member.user._id"
                    x-small
                    color="orange"
                    class="ml-2"
                  >
                    Creator
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>{{ member.user.Email }}</v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon
                  v-if="member.user.isOnline"
                  color="green"
                  small
                >
                  mdi-circle
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <!-- Group Actions -->
          <v-divider class="my-4"></v-divider>
          <div v-if="isGroupAdmin">
            <h4 class="mb-3">Group Settings</h4>
            <v-list dense>
              <v-list-item @click="editGroupInfo">
                <v-list-item-icon>
                  <v-icon>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Edit Group Info</v-list-item-title>
              </v-list-item>
              
              <v-list-item @click="addMembers">
                <v-list-item-icon>
                  <v-icon>mdi-account-plus</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Add Members</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>

          <v-divider class="my-4"></v-divider>
          <v-list dense>
            <v-list-item @click="leaveGroup" class="red--text">
              <v-list-item-icon>
                <v-icon color="red">mdi-exit-to-app</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Leave Group</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Edit Group Info Dialog -->
    <v-dialog v-model="showEditGroup" max-width="400px">
      <v-card>
        <v-card-title>Edit Group Info</v-card-title>
        <v-card-text>
          <v-form ref="editForm" v-model="editValid">
            <v-text-field
              v-model="editGroupName"
              label="Group Name"
              :rules="nameRules"
              outlined
              dense
              required
              class="mb-4"
            ></v-text-field>

            <v-textarea
              v-model="editGroupDescription"
              label="Description"
              outlined
              dense
              rows="3"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showEditGroup = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveGroupInfo"
            :disabled="!editValid"
            :loading="saving"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Members Dialog -->
    <v-dialog v-model="showAddMembers" max-width="500px">
      <v-card>
        <v-card-title>Add Members</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="memberSearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search users..."
            outlined
            dense
            hide-details
            @input="searchMembers"
            class="mb-4"
          ></v-text-field>

          <div v-if="searchResults.length > 0">
            <v-list dense>
              <v-list-item
                v-for="user in searchResults"
                :key="user._id"
                @click="addMember(user)"
                class="user-item"
                :disabled="isMemberInGroup(user._id)"
              >
                <v-list-item-avatar>
                  <v-img
                    v-if="user.profileimg"
                    :src="user.profileimg"
                  ></v-img>
                  <v-avatar v-else color="primary">
                    <v-icon>mdi-account</v-icon>
                  </v-avatar>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>{{ user.Name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ user.Email }}</v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <v-icon
                    v-if="isMemberInGroup(user._id)"
                    color="green"
                  >
                    mdi-check
                  </v-icon>
                  <v-icon
                    v-else
                    color="grey"
                  >
                    mdi-plus
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <div v-else-if="memberSearch && !loading" class="text-center pa-4">
            <p class="grey--text">No users found</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showAddMembers = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';

const url = require("@/client_config/config").url;

export default {
  name: 'ChatInfoDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    conversation: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showEditGroup: false,
      showAddMembers: false,
      editGroupName: '',
      editGroupDescription: '',
      editValid: false,
      saving: false,
      memberSearch: '',
      searchResults: [],
      loading: false,
      nameRules: [
        v => !!v || 'Group name is required',
        v => v.length >= 3 || 'Group name must be at least 3 characters',
        v => v.length <= 50 || 'Group name must be less than 50 characters'
      ]
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    isGroupAdmin() {
      if (!this.conversation || this.conversation.type !== 'group') return false;
      const currentUserId = localStorage.getItem('userID');
      return this.conversation.group.creator === currentUserId || 
             this.conversation.group.admins.includes(currentUserId);
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal && this.conversation && this.conversation.type === 'group') {
        this.editGroupName = this.conversation.group.name;
        this.editGroupDescription = this.conversation.group.description || '';
      }
    }
  },
  methods: {
    formatLastSeen(timestamp) {
      if (!timestamp) return 'unknown';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString();
    },

    editGroupInfo() {
      this.showEditGroup = true;
    },

    async saveGroupInfo() {
      if (!this.$refs.editForm.validate()) return;

      this.saving = true;
      try {
        await axios.put(`${url}/chat/groups/${this.conversation.group._id}`, {
          name: this.editGroupName,
          description: this.editGroupDescription
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // Update the conversation object
        this.conversation.group.name = this.editGroupName;
        this.conversation.group.description = this.editGroupDescription;
        
        this.showEditGroup = false;
      } catch (error) {
        console.error('Error updating group:', error);
      } finally {
        this.saving = false;
      }
    },

    addMembers() {
      this.showAddMembers = true;
    },

    searchMembers: debounce(async function() {
      if (!this.memberSearch || this.memberSearch.length < 2) {
        this.searchResults = [];
        return;
      }

      this.loading = true;
      try {
        const response = await axios.get(`${url}/chat/search/users`, {
          params: { query: this.memberSearch },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.searchResults = response.data.users;
      } catch (error) {
        console.error('Error searching users:', error);
        this.searchResults = [];
      } finally {
        this.loading = false;
      }
    }, 300),

    isMemberInGroup(userId) {
      return this.conversation.group.members.some(member => member.user._id === userId);
    },

    async addMember(user) {
      if (this.isMemberInGroup(user._id)) return;

      try {
        await axios.post(`${url}/chat/groups/${this.conversation.group._id}/members`, {
          memberIds: [user._id]
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // Add to local members list
        this.conversation.group.members.push({
          user: user,
          role: 'member',
          joinedAt: new Date()
        });

        this.memberSearch = '';
        this.searchResults = [];
      } catch (error) {
        console.error('Error adding member:', error);
      }
    },

    async leaveGroup() {
      try {
        await axios.post(`${url}/chat/groups/${this.conversation.group._id}/leave`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        this.$emit('group-left', this.conversation.group._id);
        this.close();
      } catch (error) {
        console.error('Error leaving group:', error);
      }
    },

    close() {
      this.dialog = false;
      this.showEditGroup = false;
      this.showAddMembers = false;
    }
  }
};
</script>

<style scoped>
.user-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.user-item:hover:not(.v-list-item--disabled) {
  background-color: #f5f5f5;
}

.user-item.v-list-item--disabled {
  opacity: 0.6;
}
</style> 